import { Outlet, useLocation, useNavigate } from "react-router-dom";
import css from "./index.module.scss";
import useGlobalStore from "@/store/useGlobalStore";
import { Spin } from "antd";
import logo from "@/assets/logo.png";
import { RouterProps } from "@/routes/typing";
import { routerList } from "@/routes";
import classNames from "classnames";
import { hasAuthSession } from "@/utils/auth";
import { useEffect, useRef, useState } from "react";
import Icon from "@/components/Icon";
import { clearAllGymDetailScrollPositions } from "@/utils/gym-detail-scroll";

type MenuProps = { title: string; path: string };

function getMenuNodes(tree: RouterProps[]): MenuProps[] {
  const result: MenuProps[] = [];
  function traverse(node: RouterProps) {
    if (node.handle?.menu && node.path) {
      result.push({ title: node.handle.menuTitle, path: "/" + node.path });
    }
    if (node.children) {
      node.children.forEach(child => traverse(child));
    }
  }
  tree.forEach(node => traverse(node));
  return result;
}

function isSubRoute(parentRoute: string, childRoute: string) {
  const parentRegex = new RegExp(`^${parentRoute.replace(/\//g, "\\/")}/`);
  return parentRegex.test(childRoute) || parentRoute === childRoute;
}

const Layout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { showLoading } = useGlobalStore();
  const navigate = useNavigate();
  // Cookie mode only saves non-sensitive session tokens and no longer reads HttpOnly Token.
  const token = hasAuthSession();
  const { pathname, search } = useLocation();
  const menus = getMenuNodes(routerList);
  const [secondFloor, showSecondFloor] = useState(false);
  const source = new URLSearchParams(search).get("source");

  const prevPathnameRef = useRef(pathname);

  //tab switching always returns to the first-level list page
  // When clicking the top navigation to leave the details link, clear the overall scroll position saved on the Gym details page
  const navigateFromHeader = (path: string) => {
    clearAllGymDetailScrollPositions();
    navigate(path);
  };
  const handleNavGyms = () => navigateFromHeader("/gyms");
  const handleNavUsers = () => navigateFromHeader("/users");

  useEffect(() => {
    if (token) {
      sessionStorage.removeItem("target");
    } else {
      navigate("/login");
      // source && source === "email" && pathname === "/guardianship" && sessionStorage.setItem("target", "/guardianship");
    }
    // Only executed when the authentication status changes to avoid repeated jumps and cleanup of the target every render.
  }, [navigate, token]);
  useEffect(() => {
    prevPathnameRef.current = pathname;

    if (isSubRoute("/guardianship", pathname) && source) {
      showSecondFloor(true);
    }
    const container = document.getElementById("layout-container");
    if (container) {
      container.scrollTo(0, 0);
    }
  }, [pathname]);

  if (!token) return null;

  return (
    <Spin spinning={showLoading} style={{ marginTop: 100 }} size="large">
      <div className={css.layout}>
        <header className={css.layout_header}>
          <div className={css.header_first_layer}>
            <div className={css.layout_header_logo} onClick={() => navigateFromHeader("/dashboard")}>
              <img width={62} src={logo} />
            </div>
            <nav className={css.layout_header_nav}>
              {menus.map(item => {
                // class-detail subpage also highlights Gyms navigation
                const active =
                  isSubRoute(item.path, pathname) || (item.path === "/gyms" && pathname.includes("/class-detail"));
                return (
                  <div
                    key={item.path}
                    data-testid={`nav-item-${item.path.replace(/^\//, "")}`}
                    aria-current={active ? "page" : undefined}
                    className={classNames(css.nav_item, active && css.active)}
                    // Special handling for Gyms/Users navigation - go directly to the list on the detail page, otherwise return to the last detail page
                    onClick={() => {
                      if (item.path === "/gyms") handleNavGyms();
                      else if (item.path === "/users") handleNavUsers();
                      else navigateFromHeader(item.path);
                    }}
                  >
                    {item.title}
                  </div>
                );
              })}
              {/*<img
                style={{ cursor: "pointer" }}
                src={secondFloor ? closeSecondIcon : showSecondIcon}
                width={40}
                onClick={() => showSecondFloor(!secondFloor)}
              />*/}
            </nav>
            <Icon
              name="account"
              data-testid="nav-item-account"
              size={40}
              style={{ cursor: "pointer" }}
              onClick={() => navigateFromHeader("/account")}
            />
          </div>
          <div
            className={css.header_second_floor}
            style={{ maxHeight: secondFloor ? 108 : 0, padding: secondFloor ? "16px 96px" : "0 96px" }}
          >
            <div
              className={classNames(css.second_floor_item, pathname.includes("/campaigns") && css.active)}
              onClick={() => navigateFromHeader("/campaigns")}
            >
              <div>Campaign Manager</div>
              <div>Assign credits to users for a promotional campaign</div>
            </div>
            <div
              className={classNames(css.second_floor_item, pathname.includes("/guardianship") && css.active)}
              onClick={() => navigateFromHeader("/guardianship")}
            >
              <div>Guardianship</div>
              <div>Review Guardian and minor pairings for Goer Squads</div>
            </div>
            <div
              className={classNames(css.second_floor_item, pathname.includes("/contact") && css.active)}
              onClick={() => navigateFromHeader("/contact")}
            >
              <div>Support & Help</div>
              <div>Manage FAQ</div>
            </div>
          </div>
        </header>
        <main id="layout-container" className={css.layout_main}>
          {children || <Outlet />}
        </main>
      </div>
    </Spin>
  );
};

export default Layout;
